import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

const CaseStudies: React.FC = () => {
  const caseStudies = [
    {
      id: 'smoove-move',
      title: 'Smoove Move Logistics',
      client: 'Smoove Move Logistics',
      industry: 'Moving & Logistics',
      year: '2024',
      services: ['Brand Identity', 'Web Design', 'Development'],
      description: 'Complete brand transformation including landing page rebuild, logo rebrand, and full brand identity system—delivering a modern, conversion-focused platform that reflects premium service quality.',
      image: '/case-studies/smoove-move/logo.png',
      link: '/case-studies/smoove-move',
      stats: [
        { label: 'Load Time', value: '81% faster' },
        { label: 'Mobile Score', value: '98/100' },
        { label: 'Experience', value: 'Premium' },
      ],
    },
    {
      id: 'galapay',
      title: 'GalaPay',
      client: 'GalaPay',
      industry: 'Fintech',
      year: '2024',
      services: ['Brand Identity', 'Web Design', 'Development'],
      description: 'From zero to launch: A complete brand and digital presence for a modern fintech company, featuring a pro lander and high-impact hero video.',
      image: '/case-studies/galapay/logo-full.png',
      link: '/case-studies/galapay',
      stats: [
        { label: 'Launch', value: 'Full Build' },
        { label: 'Hosting', value: 'Chipahost' },
        { label: 'Identity', value: 'Complete' },
      ],
    },
  ]

  return (
    <div className="min-h-screen bg-background text-primary">
      {/* Hero */}
      <section className="pt-40 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.h1
            className="text-6xl md:text-8xl font-display font-medium tracking-tighter text-white mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            Case Studies
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl text-neutral-400 font-light leading-relaxed max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            Transforming brands through strategic design and development.
          </motion.p>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-32">
            {caseStudies.map((study, index) => (
              <Link key={study.id} to={study.link}>
                <motion.div
                  className="group relative bg-surfaceHighlight border border-white/5 hover:border-accent/50 transition-colors duration-500"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: index * 0.2, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true }}
                >
                  <div className="grid md:grid-cols-2 gap-0">
                    {/* Content Side */}
                    <div className="p-12 md:p-20 flex flex-col justify-center order-2 md:order-1">
                      <div className="flex flex-wrap gap-3 mb-8">
                        {study.services.map((service) => (
                          <div
                            key={service}
                            className="px-4 py-1 border border-white/10 text-white rounded-full text-xs font-bold uppercase tracking-wider"
                          >
                            {service}
                          </div>
                        ))}
                      </div>

                      <h3 className="text-4xl md:text-5xl font-display font-medium mb-6 text-white group-hover:text-accent transition-colors duration-300">
                        {study.title}
                      </h3>

                      <p className="text-neutral-400 font-light mb-12 leading-relaxed text-lg">
                        {study.description}
                      </p>

                      <div className="grid grid-cols-3 gap-8 mb-12 border-t border-white/10 pt-8">
                        {study.stats.map((stat) => (
                          <div key={stat.label}>
                            <div className="text-3xl font-display font-medium mb-1 text-white">{stat.value}</div>
                            <div className="text-xs text-neutral-500 uppercase tracking-wider">{stat.label}</div>
                          </div>
                        ))}
                      </div>

                      <div className="inline-flex items-center text-sm font-bold uppercase tracking-wider text-white group-hover:gap-4 transition-all gap-2">
                        View case study
                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </div>
                    </div>

                    {/* Image Side */}
                    <div className="order-1 md:order-2 aspect-square md:aspect-auto bg-neutral-900 overflow-hidden relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10"></div>
                      <img
                        src={study.image}
                        alt={study.title}
                        className="w-full h-full object-contain p-20 group-hover:scale-110 transition-transform duration-700 ease-out"
                      />
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            className="text-5xl md:text-7xl font-display font-medium text-white mb-8 tracking-tighter"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            Ready for results like these?
          </motion.h2>
          <motion.p
            className="text-xl text-neutral-400 mb-12 font-light"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            Let's create something extraordinary together.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <Link
              to="/services#quote"
              className="inline-flex items-center px-10 py-5 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-accent hover:text-white transition-all duration-300"
            >
              Start your project
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default CaseStudies
